import { signStatement, verifyStatement } from './daemon-identity.js';
import { DOMAIN, hashCanonical } from './hash.js';
import { jwkThumbprint } from './keys.js';
export function createRevocationList(input) {
    return {
        v: 1,
        type: 'viko.revocations',
        seq: input.seq,
        issuedAt: input.now ?? Date.now(),
        devices: [...new Set(input.devices ?? [])].sort(),
        credentials: [...new Set(input.credentials ?? [])].sort(),
    };
}
export async function createRevocationChallenge(list) {
    return hashCanonical(DOMAIN.revocations, list);
}
export function isRevocationList(v) {
    if (typeof v !== 'object' || v === null)
        return false;
    const l = v;
    const strings = (a) => Array.isArray(a) && a.every((x) => typeof x === 'string');
    return (l.v === 1 &&
        l.type === 'viko.revocations' &&
        Number.isSafeInteger(l.seq) &&
        l.seq > 0 &&
        Number.isSafeInteger(l.issuedAt) &&
        strings(l.devices) &&
        strings(l.credentials));
}
/**
 * The next cumulative list: strictly newer seq, union of everything already revoked plus the
 * additions. Callers sign it with a pinned passkey that it does not revoke.
 */
export function nextRevocationList(current, add) {
    return createRevocationList({
        seq: current.revocationSeq + 1,
        devices: [...current.revokedDeviceIds(), ...(add.devices ?? [])],
        credentials: [...current.revokedCredentialIds(), ...(add.credentials ?? [])],
        ...(add.now === undefined ? {} : { now: add.now }),
    });
}
// ---- signed daemon revocation-sequence reports ------------------------------------------------
export const REVOCATION_REPORT_DOMAIN = 'viko/daemon-revocation-report/v1';
function validReport(r) {
    if (!r || typeof r !== 'object')
        return false;
    const keys = Object.keys(r).sort().join();
    if (keys !==
        'accountId,daemonKeyId,directoryRevision,epoch,machineId,reportedAt,revocationSeq,version')
        return false;
    const counter = (n, min) => typeof n === 'number' && Number.isSafeInteger(n) && n >= min;
    return (r.version === 1 &&
        [r.accountId, r.machineId, r.daemonKeyId].every((s) => typeof s === 'string' && s.length > 0 && s.length <= 256) &&
        counter(r.revocationSeq, 0) &&
        counter(r.directoryRevision, 0) &&
        counter(r.epoch, 0) &&
        counter(r.reportedAt, 0));
}
export async function signRevocationReport(signer, report) {
    if (!validReport(report) || report.daemonKeyId !== signer.keyId)
        throw new Error('invalid revocation report');
    const copyOf = structuredClone(report);
    return {
        report: copyOf,
        signature: await signStatement(signer, REVOCATION_REPORT_DOMAIN, copyOf),
    };
}
/** `daemonPublicKey` MUST come from the endpoint's own approved enrollment pin, not the relay. */
export async function verifyRevocationReport(signed, daemonPublicKey, expected) {
    const copyOf = structuredClone(signed);
    if (!copyOf ||
        typeof copyOf !== 'object' ||
        Object.keys(copyOf).sort().join() !== 'report,signature' ||
        !validReport(copyOf.report) ||
        typeof copyOf.signature !== 'string')
        throw new Error('invalid revocation report');
    const r = copyOf.report;
    if (r.accountId !== expected.accountId || r.machineId !== expected.machineId)
        throw new Error('revocation report context mismatch');
    if (r.daemonKeyId !== (await jwkThumbprint(daemonPublicKey)))
        throw new Error('revocation report key mismatch');
    if (!(await verifyStatement(daemonPublicKey, REVOCATION_REPORT_DOMAIN, r, copyOf.signature)))
        throw new Error('invalid revocation report signature');
    return r;
}
//# sourceMappingURL=revocation.js.map